// The persona-filtered view every navigation surface renders: the top bar, the
// sidebar and the DashAI home grids.
//
// The server decides it. `dashoneNavigation` resolves what the workspace
// entitles and splits it by the user's persona, so this hook only maps the ids
// it returns onto the catalogue entries the surfaces draw, in catalogue order.
// An id the catalogue does not carry has nothing to draw and is skipped.

import { useMemo } from "react";
import { useSelector } from "react-redux";
import { workflowAgents } from "@dashai/components/dashai/WorkflowAgentsGrid";
import { useNavigation } from "@dashai/hooks/useNavigation";
import { usePersonaConfig } from "@/hooks/usePersonaConfig";
import { DASHBOARD_CARDS } from "@/data/dashboardCards";
import type { DashboardCard } from "@/data/dashboardCards";
import type { PersonaConfig } from "@/data/personaConfigTypes";
import { SFA_BASE_ID, catalogueEntry, makeVariantEntries } from "@dashai/utils/sfaVariants";
import type { BrowseEntry } from "@dashai/utils/sfaVariants";

export interface PersonaVisibility {
  // "error" = the workflows could not be resolved: the renderer says so rather
  // than showing an eternal load. Dashboards still render when the server
  // resolved them.
  status: "loading" | "ready" | "error";
  config: PersonaConfig | null;
  // Whether "Add to home" can be offered. A config can be READ without being
  // editable: the Dashmote-staff Power User default is synthesised per read
  // and owned by nobody, so pinning to it has nothing to write to.
  canPersonalize: boolean;
  dashboards: { inPersona: DashboardCard[]; notInPersona: DashboardCard[] };
  // Browse ENTRIES, not raw agents: a per-franchise audit entry is not a
  // catalogue agent and must not be substitutable for one (see BrowseEntry).
  workflows: { inPersona: BrowseEntry[]; notInPersona: BrowseEntry[] };
  // Ids the user added on top of the persona's set ("Add to home"), kept apart
  // by kind because dashboard card ids and workflow ids are separate
  // namespaces that nearly collide.
  userAdded: {
    dashboards: ReadonlySet<string>;
    workflows: ReadonlySet<string>;
  };
}

const NONE: ReadonlySet<string> = new Set();

export function usePersonaVisibility(currentClient: string | null): PersonaVisibility {
  const { config: ownConfig, canPersonalize } = usePersonaConfig();
  const isImpersonating = useSelector((state: { auth?: { isImpersonating?: boolean } }) =>
    Boolean(state.auth?.isImpersonating)
  );
  // Viewing as a user is unsplit, so there is no persona to show.
  const config = isImpersonating ? null : ownConfig;
  const { data, isError } = useNavigation(currentClient);

  return useMemo(() => {
    if (!data) {
      return {
        status: isError ? ("error" as const) : ("loading" as const),
        config,
        canPersonalize,
        dashboards: { inPersona: [], notInPersona: [] },
        workflows: { inPersona: [], notInPersona: [] },
        userAdded: { dashboards: NONE, workflows: NONE },
      };
    }

    const inDashboards = new Set(data.dashboards.in_persona);
    const moreDashboards = new Set(data.dashboards.not_in_persona);
    const dashboards = {
      inPersona: DASHBOARD_CARDS.filter((card) => inDashboards.has(card.id)),
      notInPersona: DASHBOARD_CARDS.filter((card) => moreDashboards.has(card.id)),
    };

    const inWorkflows = new Set(data.workflows.in_persona);
    const moreWorkflows = new Set(data.workflows.not_in_persona);
    const workflows = { inPersona: [] as BrowseEntry[], notInPersona: [] as BrowseEntry[] };
    for (const agent of workflowAgents) {
      // The per-franchise audits take the generic audit's place in the list.
      if (agent.id === SFA_BASE_ID && data.sfa_variants.length > 0) {
        workflows.inPersona.push(...makeVariantEntries(agent, data.sfa_variants));
      }
      if (inWorkflows.has(agent.id)) workflows.inPersona.push(catalogueEntry(agent));
      else if (moreWorkflows.has(agent.id)) workflows.notInPersona.push(catalogueEntry(agent));
    }

    return {
      status: data.registry_error ? ("error" as const) : ("ready" as const),
      config,
      canPersonalize,
      dashboards,
      workflows,
      userAdded: {
        dashboards: new Set(data.user_added.dashboards),
        workflows: new Set(data.user_added.workflows),
      },
    };
  }, [data, isError, config, canPersonalize]);
}
